import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../styles/globals';

const HeroContainer = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.darkBackground} 100%);
  color: ${colors.white};
  padding: 80px 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
    min-height: 80vh;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
`;

const HeroText = styled.div`
  @media (max-width: 768px) {
    order: 2;
  }
`;

const HeroImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    order: 1;
  }
`;

const ImageContainer = styled.div`
  width: 400px;
  height: 500px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    width: 300px;
    height: 375px;
  }

  @media (max-width: 480px) {
    width: 250px;
    height: 312px;
  }
`;

const HeroImageElement = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 20px;
`;

const MainTitle = styled.h1`
  font-family: ${fonts.playfair};
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.1;
  background: linear-gradient(135deg, ${colors.white} 0%, ${colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  font-family: ${fonts.inter};
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  font-weight: 400;
  margin-bottom: 40px;
  line-height: 1.5;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 30px;
    text-align: center;
  }
`;

const GiftText = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 30px;
  }
`;

const GiftTitle = styled.h3`
  font-family: ${fonts.playfair};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${colors.accent};
`;

const GiftDescription = styled.p`
  font-family: ${fonts.inter};
  font-size: 1rem;
  opacity: 0.9;
  line-height: 1.4;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, ${colors.accent} 0%, #E67E22 100%);
  color: ${colors.white};
  font-family: ${fonts.inter};
  font-size: 1.1rem;
  font-weight: 600;
  padding: 18px 40px;
  border-radius: 50px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(243, 156, 107, 0.3);
  position: relative;
  overflow: hidden;
  width: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(243, 156, 107, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 16px 32px;
    margin: 0 auto;
    display: block;
  }
`;

function Hero() {
  const handleCTAClick = () => {
    // Scroll to CTA section
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer>
      <HeroContent>
        <HeroText>
          <MainTitle>
            ¿Y si hablar en público no fuera una tortura, sino una liberación?
          </MainTitle>
          
          <Subtitle>
            21 días. Una transformación emocional y comunicativa para liberar tu voz, 
            sanar tus bloqueos y hablar con autenticidad.
          </Subtitle>

          <GiftText>
            <GiftTitle>Agenda tu sesión gratuita de valoración y recibe de regalo la guía:</GiftTitle>
            <GiftDescription>
              "3 claves para liberar tu voz con autenticidad"
            </GiftDescription>
          </GiftText>

          <CTAButton onClick={handleCTAClick}>
            Sí, quiero transformar mi forma de comunicarme
          </CTAButton>
        </HeroText>

        <HeroImage>
          <ImageContainer>
            <HeroImageElement
              src="/images/mejorada_proporcional.webp"
              alt="Eduardo Gaoch - Coach en Oratoria"
            />
          </ImageContainer>
        </HeroImage>
      </HeroContent>
    </HeroContainer>
  );
}

export default Hero; 